import { useState } from 'react';
import BalanceIcon from '@mui/icons-material/Balance';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Fade,
  Backdrop,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import {
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
  EmptyState,
} from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { MonthlyClientBreakdown, DrillDownRow, CogsDrillDownRow, RevenueForecastDrillDownRow } from '@/services/financeClients';
import { QbConnection } from '@/services/financeQuickBooks';
import { Paths } from '@/types/routes';
import { DatePreset, DateFilter } from './FinanceXDashboard.container';

type TopVendor = { vendorId: string; vendorName: string; total: number; txCount: number };
type BurnEntry = { month: string; burn: number; cumulative: number };

type Props = {
  revenue: number;
  expenses: number;
  netIncome: number;
  avgMonthlyBurn: number;
  uncategorizedCount: number;
  spendByCategory: Array<{ categoryId: string; categoryName: string; total: number }>;
  topVendors: TopVendor[];
  monthlyTotals: Array<{ month: string; revenue: number; expenses: number }>;
  monthlyBurn: BurnEntry[];
  categoryColorMap: Map<string, string>;
  monthlyClientRevenue: MonthlyClientBreakdown;
  reauthConnections: Array<{ id: string; institutionName: string }>;
  staleQbConnections: QbConnection[];
  isLoading: boolean;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  drillDown: {
    open: boolean;
    type: 'revenue' | 'cogs' | 'ops' | 'opex';
    month: string;
    label: string;
    rows: DrillDownRow[];
    cogsRows: CogsDrillDownRow[];
    forecastRows: RevenueForecastDrillDownRow[];
    loading: boolean;
  };
  onDrillDown: (type: 'revenue' | 'cogs' | 'ops' | 'opex', month: string, label: string) => void;
  onCloseDrillDown: () => void;
};

const fmt = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const fmtFull = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

function fmtMonth(yyyyMm: string): string {
  const parts = yyyyMm.split('-');
  return `${MONTH_LABELS[parts[1]] ?? parts[1]} '${parts[0].slice(2)}`;
}

/** Short month label — shows "Jan", "Feb", etc. Adds year suffix only on Jan or when year changes. */
function fmtMonthShort(yyyyMm: string, prevMonth?: string): string {
  const parts = yyyyMm.split('-');
  const label = MONTH_LABELS[parts[1]] ?? parts[1];
  const showYear = parts[1] === '01' || (prevMonth && prevMonth.split('-')[0] !== parts[0]);
  return showYear ? `${label} '${parts[0].slice(2)}` : label;
}

const VENDOR_COLORS = [
  '#054AD9',
  '#8B5CF6',
  '#F59E0B',
  '#10B981',
  '#EC4899',
  '#06B6D4',
  '#EF4444',
  '#84CC16',
  '#6366F1',
  '#FF7C43',
];

const FinanceXDashboardComponent = function ({
  revenue,
  expenses,
  netIncome,
  avgMonthlyBurn,
  uncategorizedCount,
  spendByCategory,
  topVendors,
  monthlyTotals,
  monthlyBurn,
  categoryColorMap,
  monthlyClientRevenue,
  reauthConnections,
  staleQbConnections,
  isLoading,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
  drillDown,
  onDrillDown,
  onCloseDrillDown,
}: Props): JSX.Element {
  const { isMobile } = useBreakpoint();

  // How the OpEx drill-down is grouped. Vendor is the default because the
  // question it answers — "what are we spending on Anthropic in total" — is not
  // answerable from the category view, where one provider's charges scatter
  // across many small subscription lines.
  const [opexGroupBy, setOpexGroupBy] = useState<'vendor' | 'category'>('vendor');

  // "3 days" / "20 days" for the QuickBooks banner. Undefined when the
  // connection has never synced at all, so the copy stays truthful.
  const oldestQbSync = staleQbConnections
    .map((c) => c.lastSyncedAt)
    .filter((d): d is string => !!d)
    .sort()[0];
  const qbStaleDays = oldestQbSync
    ? Math.floor((Date.now() - new Date(oldestQbSync).getTime()) / 864e5)
    : null;
  // Under a day the duration is omitted rather than rendered as "in 0 days" —
  // that case is a revoked/never-synced connection, where the age is not the point.
  let qbStaleFor = '';
  if (qbStaleDays !== null && qbStaleDays >= 1) {
    qbStaleFor = `${qbStaleDays} day${qbStaleDays === 1 ? '' : 's'}`;
  }

  // --- Revenue vs Expenses bar chart ---
  const revenueExpenseOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) =>
        params.map((p: any) => `${p.marker} ${p.seriesName}: ${fmtFull(p.value)}`).join('<br/>'),
    },
    legend: {
      data: ['Revenue', 'Expenses'],
      bottom: 0,
      textStyle: { fontSize: 12, color: '#71717A' },
    },
    grid: { left: 65, right: 20, top: 20, bottom: 44 },
    xAxis: {
      type: 'category' as const,
      data: monthlyTotals.map((m) => fmtMonth(m.month)),
      axisLabel: { fontSize: 11, color: '#71717A' },
      axisLine: { lineStyle: { color: '#E4E4E7' } },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: '#F4F4F5' } },
    },
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: monthlyTotals.map((m) => m.revenue),
        itemStyle: { color: '#10B981', borderRadius: [4, 4, 0, 0] },
        barGap: '10%',
      },
      {
        name: 'Expenses',
        type: 'bar',
        data: monthlyTotals.map((m) => m.expenses),
        itemStyle: { color: '#EF4444', borderRadius: [4, 4, 0, 0] },
      },
    ],
  };

  // --- Net burn trend (area chart) ---
  const burnOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) =>
        params.map((p: any) => `${p.marker} ${p.seriesName}: ${fmtFull(p.value)}`).join('<br/>'),
    },
    legend: {
      data: ['Monthly Net Burn', 'Cumulative'],
      bottom: 0,
      textStyle: { fontSize: 12, color: '#71717A' },
    },
    grid: { left: 65, right: 20, top: 20, bottom: 44 },
    xAxis: {
      type: 'category' as const,
      data: monthlyBurn.map((m) => fmtMonth(m.month)),
      axisLabel: { fontSize: 11, color: '#71717A' },
      axisLine: { lineStyle: { color: '#E4E4E7' } },
    },
    yAxis: {
      type: 'value' as const,
      axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: '#F4F4F5' } },
    },
    series: [
      {
        name: 'Monthly Net Burn',
        type: 'bar',
        data: monthlyBurn.map((m) => m.burn),
        itemStyle: {
          color: (p: any) => (p.value > 0 ? '#EF4444' : '#10B981'),
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: 'Cumulative',
        type: 'line',
        data: monthlyBurn.map((m) => m.cumulative),
        lineStyle: { color: '#054AD9', width: 2 },
        itemStyle: { color: '#054AD9' },
        symbol: 'circle',
        symbolSize: 5,
        smooth: true,
      },
    ],
  };

  // --- Spend by category donut ---
  const pieOption = {
    tooltip: {
      trigger: 'item',
      formatter: (p: any) => `${p.name}: ${fmtFull(p.value)} (${p.percent}%)`,
    },
    series: [
      {
        type: 'pie',
        radius: ['48%', '72%'],
        center: ['50%', '50%'],
        data: spendByCategory
          .sort((a, b) => b.total - a.total)
          .map((item) => ({
            name: item.categoryName,
            value: Math.round(item.total * 100) / 100,
            itemStyle: { color: categoryColorMap.get(item.categoryName) ?? '#6B7280' },
          })),
        label: { show: true, fontSize: 11, formatter: '{b}: {d}%', color: '#4A4B4E' },
        emphasis: { label: { show: true, fontWeight: 700 } },
      },
    ],
  };

  // --- Date filter ---
  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: 13,
            px: 1.5,
            py: 0.5,
            borderColor: '#E4E4E7',
            '&.Mui-selected': {
              backgroundColor: '#054AD915',
              color: '#054AD9',
              borderColor: '#054AD9',
              '&:hover': { backgroundColor: '#054AD925' },
            },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );

  // Total spend for percentage calc
  const totalExpenses = expenses;

  return (
    <>
      <PageHeader title="Financial Overview" actions={dateFilterActions} />

      {/* Bank connection needs re-authentication */}
      {reauthConnections.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18, color: '#B91C1C', flexShrink: 0 }} />
          <Box sx={{ fontSize: 13, color: '#7F1D1D', flex: 1, minWidth: 200 }}>
            <strong>{reauthConnections.map((c) => c.institutionName).join(', ')}</strong>{' '}
            {reauthConnections.length === 1 ? 'needs' : 'need'} to be reconnected — Plaid can no
            longer sync {reauthConnections.length === 1 ? 'this account' : 'these accounts'}, so new
            transactions aren&apos;t importing.
          </Box>
          <Link
            to="/financex/bank-accounts"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Reconnect
          </Link>
        </Box>
      )}

      {/* QuickBooks has stopped syncing. Amber rather than red: unlike a dead
          Plaid connection this usually self-heals on the next nightly run, but
          it still means invoices and balances on screen are out of date. */}
      {staleQbConnections.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: '#FFFBEB',
            border: '1px solid #FDE68A',
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18, color: '#B45309', flexShrink: 0 }} />
          <Box sx={{ fontSize: 13, color: '#78350F', flex: 1, minWidth: 200 }}>
            <strong>QuickBooks hasn&apos;t synced{qbStaleFor ? ` in ${qbStaleFor}` : ''}</strong> —
            invoices and open balances shown here may be out of date.
          </Box>
          <Link
            to={Paths.SETTINGS_INTEGRATIONS}
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Check connection
          </Link>
        </Box>
      )}

      {/* Uncategorized alert */}
      {uncategorizedCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18, color: '#D97706' }} />
          <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            <strong>{uncategorizedCount}</strong> transaction{uncategorizedCount !== 1 ? 's' : ''}{' '}
            need{uncategorizedCount === 1 ? 's' : ''} review — missing vendor or category.
          </Box>
          <Link
            to="/financex/transactions"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Review
          </Link>
        </Box>
      )}

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard
          label="Revenue"
          value={fmt(revenue)}
          color="#10B981"
          indicator={<TrendingUpIcon sx={{ fontSize: 16 }} />}
          isLoading={isLoading}
        />
        <MetricCard
          label="Expenses"
          value={fmt(expenses)}
          color="#EF4444"
          indicator={<TrendingDownIcon sx={{ fontSize: 16 }} />}
          isLoading={isLoading}
        />
        <MetricCard
          label="Net Income"
          value={fmt(netIncome)}
          subtitle={netIncome >= 0 ? 'Profitable' : 'Net Loss'}
          color={netIncome >= 0 ? '#10B981' : '#EF4444'}
          indicator={<BalanceIcon sx={{ fontSize: 16 }} />}
          highlighted={netIncome < 0}
          isLoading={isLoading}
        />
        <MetricCard
          label="Avg Monthly Burn"
          value={fmt(Math.abs(avgMonthlyBurn))}
          subtitle={avgMonthlyBurn > 0 ? 'Net cash out' : 'Net cash in'}
          color={avgMonthlyBurn > 0 ? '#F59E0B' : '#10B981'}
          indicator={<LocalFireDepartmentIcon sx={{ fontSize: 16 }} />}
          isLoading={isLoading}
        />
      </Box>

      {/* Revenue vs Expenses + Net Burn */}
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}
      >
        <SectionCard title="Revenue vs Expenses">
          {(() => {
            if (isLoading) return <LoadingState message="Loading chart..." />;
            if (monthlyTotals.length === 0)
              return <EmptyState title="No data" description="No transactions for this period." />;
            return <ReactECharts option={revenueExpenseOption} style={{ height: 300 }} />;
          })()}
        </SectionCard>
        <SectionCard title="Net Burn Rate" subtitle="Monthly net cash flow (expenses − revenue)">
          {(() => {
            if (isLoading) return <LoadingState message="Loading chart..." />;
            if (monthlyBurn.length === 0)
              return <EmptyState title="No data" description="No transactions for this period." />;
            return <ReactECharts option={burnOption} style={{ height: 300 }} />;
          })()}
        </SectionCard>
      </Box>

      {/* Monthly Client Breakdown — Revenue, COGS, Profit per client + forecast */}
      {monthlyClientRevenue.months.length > 0 &&
        (() => {
          const {
            months,
            forecastStartMonth,
            totalRevenue: tRev,
            totalCogs: tCogs,
            totalActualCogs: tActual,
            totalOpsPlan: tOpsPlan,
            totalOpsActual: tOpsActual,
            totalOpex: tOpex,
            clients,
          } = monthlyClientRevenue;
          const hasActualCogs = Object.values(tActual).some((v) => v > 0);
          const hasOps = Object.values(tOpsPlan).some((v) => v > 0) || Object.values(tOpsActual).some((v) => v > 0);
          const isForecast = (m: string): boolean =>
            !!forecastStartMonth && m >= forecastStartMonth;
          const forecastBg = '#FFFBEB';
          const colCount = months.length + 2;

          const cellStyle = (m: string, color?: string): React.CSSProperties => ({
            ...(isForecast(m) ? { backgroundColor: forecastBg } : {}),
            ...(color ? { color } : {}),
          });

          const sumValues = (rec: Record<string, number>): number =>
            Object.values(rec).reduce((s, v) => s + v, 0);

          // Profit lines use ACTUAL contractor cost for historical months where a
          // payroll was uploaded; fall back to PLAN (rate card) for forecast months
          // and historical months with no payroll yet.
          const effectiveCogs = (m: string): number =>
            !isForecast(m) && (tActual[m] ?? 0) > 0 ? tActual[m] : (tCogs[m] ?? 0);
          const totalEffectiveCogs = months.reduce((s, m) => s + effectiveCogs(m), 0);

          // Which basis a month's COGS actually came from. Without this the table
          // silently mixes measured payroll with rate-card estimates, so two
          // months sitting side by side are not comparable and nothing says so.
          const cogsBasis = (m: string): 'actual' | 'plan' =>
            !isForecast(m) && (tActual[m] ?? 0) > 0 ? 'actual' : 'plan';
          const planMonths = months.filter((m) => !isForecast(m) && cogsBasis(m) === 'plan');
          const actualMonths = months.filter((m) => cogsBasis(m) === 'actual');
          /**
           * Only HISTORICAL rate-card months get the marker. Forecast months are
           * estimates by definition and already carry the amber column and `*`,
           * so marking them again would just add noise.
           */
          const isEstimated = (m: string): boolean => !isForecast(m) && cogsBasis(m) === 'plan';
          const basisStyle = (m: string): React.CSSProperties =>
            isEstimated(m) ? { opacity: 0.55, fontStyle: 'italic' } : {};
          const basisTitle = (m: string): string =>
            isEstimated(m)
              ? 'Estimated from contractor rate cards — no payroll report uploaded for this month'
              : 'Measured from the uploaded payroll report';

          // Operational people (internal staff): actual payroll where available, else plan.
          const effectiveOps = (m: string): number =>
            !isForecast(m) && (tOpsActual[m] ?? 0) > 0 ? tOpsActual[m] : (tOpsPlan[m] ?? 0);
          const totalEffectiveOps = months.reduce((s, m) => s + effectiveOps(m), 0);

          // Actual payroll not attributed to any client (unmatched profile or no
          // team allocation). Surfaced so the per-client view isn't silently short.
          const clientActual = (m: string): number =>
            clients.reduce((s, c) => s + (c.monthlyActualCogs[m] ?? 0), 0);
          const unallocatedActual = (m: string): number =>
            Math.max(0, (tActual[m] ?? 0) - clientActual(m));
          const totalUnallocatedActual = months.reduce((s, m) => s + unallocatedActual(m), 0);
          const hasUnallocated = hasActualCogs && totalUnallocatedActual > 1;

          return (
            <Box sx={{ mb: 3 }}>
              <SectionCard title="Monthly Breakdown">
                {/* State the basis up front. Measured payroll and rate-card
                    estimates are not comparable, and the table silently mixed
                    them — two months side by side could differ by basis rather
                    than by anything that happened in the business. */}
                {planMonths.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 1.5,
                      p: 1.25,
                      borderRadius: '8px',
                      backgroundColor: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      fontSize: 12,
                      color: '#78350F',
                      lineHeight: 1.5,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#B45309', flexShrink: 0, mt: '1px' }} />
                    <Box>
                      <strong>COGS mixes two bases.</strong>{' '}
                      {actualMonths.length > 0 ? (
                        <>
                          {actualMonths.map(fmtMonth).join(', ')}{' '}
                          {actualMonths.length === 1 ? 'is' : 'are'} measured from uploaded payroll.{' '}
                        </>
                      ) : null}
                      {planMonths.map(fmtMonth).join(', ')}{' '}
                      {planMonths.length === 1 ? 'has' : 'have'} no payroll report, so COGS and
                      margin are <em>estimated from rate cards</em> — shown dimmed and marked{' '}
                      <strong>~</strong>. Month-over-month comparison across the two is not
                      like-for-like.
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    overflowX: 'auto',
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                      minWidth: months.length * 100 + 180,
                    },
                    '& th': {
                      textAlign: 'right',
                      padding: '6px 10px',
                      color: '#71717A',
                      fontWeight: 500,
                      fontSize: 11,
                      borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                      whiteSpace: 'nowrap',
                    },
                    '& th:first-of-type': { textAlign: 'left' },
                    '& td': {
                      textAlign: 'right',
                      padding: '5px 10px',
                      borderBottom: '1px solid hsla(0,0%,0%,0.04)',
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                    },
                    '& td:first-of-type': { textAlign: 'left', fontWeight: 500 },
                  }}
                >
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', minWidth: 140 }}>&nbsp;</th>
                        {months.map((m, idx) => (
                          <th
                            key={m}
                            style={
                              isForecast(m) ? { backgroundColor: forecastBg, color: '#92400E' } : {}
                            }
                          >
                            {fmtMonthShort(m, idx > 0 ? months[idx - 1] : undefined)}
                            {isForecast(m) ? ' *' : ''}
                          </th>
                        ))}
                        <th style={{ fontWeight: 700 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* ── Summary rows ── */}
                      <tr style={{ backgroundColor: '#F0FDF4' }}>
                        <td style={{ fontWeight: 700, color: '#10B981' }}>Total Revenue</td>
                        {months.map((m) => (
                          <td key={m} style={{ ...cellStyle(m, '#10B981'), fontWeight: 500 }}>
                            {tRev[m] ? (
                              <button
                                type="button"
                                onClick={() => onDrillDown('revenue', m, `Revenue — ${fmtMonth(m)}`)}
                                style={{ all: 'unset', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                              >
                                {fmt(tRev[m])}
                              </button>
                            ) : '—'}
                          </td>
                        ))}
                        <td style={{ fontWeight: 700, color: '#10B981' }}>
                          {fmt(sumValues(tRev))}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#FEF2F2' }}>
                        <td style={{ fontWeight: 700, color: '#EF4444' }}>COGS</td>
                        {months.map((m) => {
                          const cogs = effectiveCogs(m);
                          return (
                            <td
                              key={m}
                              title={basisTitle(m)}
                              style={{ ...cellStyle(m, '#EF4444'), fontWeight: 500, ...basisStyle(m) }}
                            >
                              {cogs ? (
                                <button
                                  type="button"
                                  onClick={() => onDrillDown('cogs', m, `COGS — ${fmtMonth(m)}`)}
                                  style={{ all: 'unset', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                                >
                                  {fmt(cogs)}
                                </button>
                              ) : '—'}
                              {cogs && isEstimated(m) ? ' ~' : ''}
                            </td>
                          );
                        })}
                        <td style={{ fontWeight: 700, color: '#EF4444' }}>
                          {fmt(totalEffectiveCogs)}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#EFF6FF' }}>
                        <td style={{ fontWeight: 700, color: '#054AD9' }}>Gross Margin</td>
                        {months.map((m) => {
                          const rev = tRev[m] ?? 0;
                          const cogs = effectiveCogs(m);
                          const margin = rev - cogs;
                          return (
                            <td
                              key={m}
                              title={basisTitle(m)}
                              style={{
                                ...cellStyle(m),
                                fontWeight: 500,
                                color: margin >= 0 ? '#054AD9' : '#EF4444',
                                ...basisStyle(m),
                              }}
                            >
                              {rev || cogs ? fmt(margin) : '—'}
                              {(rev || cogs) && isEstimated(m) ? ' ~' : ''}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            fontWeight: 700,
                            color: sumValues(tRev) - totalEffectiveCogs >= 0 ? '#054AD9' : '#EF4444',
                          }}
                        >
                          {fmt(sumValues(tRev) - totalEffectiveCogs)}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#EFF6FF' }}>
                        <td style={{ fontWeight: 700, color: '#054AD9' }}>Gross Margin %</td>
                        {months.map((m) => {
                          const rev = tRev[m] ?? 0;
                          const cogs = effectiveCogs(m);
                          const pct = rev > 0 ? Math.round(((rev - cogs) / rev) * 100) : 0;
                          return (
                            <td
                              key={m}
                              style={{
                                ...cellStyle(m),
                                fontWeight: 500,
                                color: pct >= 0 ? '#054AD9' : '#EF4444',
                              }}
                            >
                              {rev || cogs ? `${pct}%` : '—'}
                            </td>
                          );
                        })}
                        {(() => {
                          const totalRev = sumValues(tRev);
                          const totalPct = totalRev > 0 ? Math.round(((totalRev - totalEffectiveCogs) / totalRev) * 100) : 0;
                          return (
                            <td style={{ fontWeight: 700, color: totalPct >= 0 ? '#054AD9' : '#EF4444' }}>
                              {totalPct}%
                            </td>
                          );
                        })()}
                      </tr>
                      {/* ── Operational People (internal staff) ── */}
                      {hasOps && (
                        <tr style={{ backgroundColor: '#F5F3FF' }}>
                          <td style={{ fontWeight: 700, color: '#7C3AED' }}>Operational People</td>
                          {months.map((m) => {
                            const ops = effectiveOps(m);
                            return (
                              <td key={m} style={{ ...cellStyle(m, '#7C3AED'), fontWeight: 500 }}>
                                {ops ? (
                                  <button
                                    type="button"
                                    onClick={() => onDrillDown('ops', m, `Operational People — ${fmtMonth(m)}`)}
                                    style={{ all: 'unset', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                                  >
                                    {fmt(ops)}
                                  </button>
                                ) : '—'}
                              </td>
                            );
                          })}
                          <td style={{ fontWeight: 700, color: '#7C3AED' }}>{fmt(totalEffectiveOps)}</td>
                        </tr>
                      )}
                      {/* ── Operating Expenses ── */}
                      <tr style={{ backgroundColor: '#FFF7ED' }}>
                        <td style={{ fontWeight: 700, color: '#EA580C' }}>Operating Expenses</td>
                        {months.map((m) => (
                          <td key={m} style={{ ...cellStyle(m, '#EA580C'), fontWeight: 500 }}>
                            {tOpex[m] ? (
                              <button
                                type="button"
                                onClick={() => onDrillDown('opex', m, `Operating Expenses — ${fmtMonth(m)}`)}
                                style={{ all: 'unset', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}
                              >
                                {fmt(tOpex[m])}
                              </button>
                            ) : '—'}
                          </td>
                        ))}
                        <td style={{ fontWeight: 700, color: '#EA580C' }}>
                          {fmt(sumValues(tOpex))}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#FFF7ED' }}>
                        <td style={{ fontWeight: 700, color: '#EA580C' }}>Operating Expenses %</td>
                        {months.map((m) => {
                          const rev = tRev[m] ?? 0;
                          const opex = tOpex[m] ?? 0;
                          const pct = rev > 0 ? Math.round((opex / rev) * 100) : 0;
                          return (
                            <td
                              key={m}
                              style={{ ...cellStyle(m), fontWeight: 500, color: '#EA580C' }}
                            >
                              {rev || opex ? `${pct}%` : '—'}
                            </td>
                          );
                        })}
                        {(() => {
                          const totalRev = sumValues(tRev);
                          const totalPct = totalRev > 0 ? Math.round((sumValues(tOpex) / totalRev) * 100) : 0;
                          return (
                            <td style={{ fontWeight: 700, color: '#EA580C' }}>
                              {totalPct}%
                            </td>
                          );
                        })()}
                      </tr>

                      {/* ── EBITDA ── */}
                      <tr style={{ backgroundColor: '#F0F9FF' }}>
                        <td style={{ fontWeight: 700, color: '#0369A1' }}>EBITDA</td>
                        {months.map((m) => {
                          const ebitda = (tRev[m] ?? 0) - effectiveCogs(m) - effectiveOps(m) - (tOpex[m] ?? 0);
                          const hasData = (tRev[m] ?? 0) > 0 || effectiveCogs(m) > 0 || effectiveOps(m) > 0 || (tOpex[m] ?? 0) > 0;
                          let ebitdaColor = '#C5C5C7';
                          if (hasData) ebitdaColor = ebitda >= 0 ? '#0369A1' : '#EF4444';
                          return (
                            <td
                              key={m}
                              style={{
                                ...cellStyle(m),
                                fontWeight: 700,
                                color: ebitdaColor,
                              }}
                            >
                              {hasData ? fmt(ebitda) : '—'}
                            </td>
                          );
                        })}
                        {(() => {
                          const totalEbitda = sumValues(tRev) - totalEffectiveCogs - totalEffectiveOps - sumValues(tOpex);
                          return (
                            <td style={{ fontWeight: 700, color: totalEbitda >= 0 ? '#0369A1' : '#EF4444' }}>
                              {fmt(totalEbitda)}
                            </td>
                          );
                        })()}
                      </tr>
                      <tr style={{ backgroundColor: '#F0F9FF' }}>
                        <td style={{ fontWeight: 700, color: '#0369A1' }}>EBITDA %</td>
                        {months.map((m) => {
                          const rev = tRev[m] ?? 0;
                          const ebitda = rev - effectiveCogs(m) - effectiveOps(m) - (tOpex[m] ?? 0);
                          const pct = rev > 0 ? Math.round((ebitda / rev) * 100) : 0;
                          const hasData = rev > 0 || effectiveCogs(m) > 0 || effectiveOps(m) > 0 || (tOpex[m] ?? 0) > 0;
                          let pctColor = '#C5C5C7';
                          if (hasData) pctColor = pct >= 0 ? '#0369A1' : '#EF4444';
                          return (
                            <td
                              key={m}
                              style={{
                                ...cellStyle(m),
                                fontWeight: 700,
                                color: pctColor,
                              }}
                            >
                              {hasData ? `${pct}%` : '—'}
                            </td>
                          );
                        })}
                        {(() => {
                          const totalRev = sumValues(tRev);
                          const totalEbitda = totalRev - totalEffectiveCogs - totalEffectiveOps - sumValues(tOpex);
                          const totalPct = totalRev > 0 ? Math.round((totalEbitda / totalRev) * 100) : 0;
                          return (
                            <td style={{ fontWeight: 700, color: totalPct >= 0 ? '#0369A1' : '#EF4444' }}>
                              {totalPct}%
                            </td>
                          );
                        })()}
                      </tr>

                      {/* ── Per-client sections ── */}
                      {clients.map((client) => {
                        const revTotal = sumValues(client.monthlyRevenue);
                        // Actual for months the company has payroll for; plan otherwise.
                        const effCogs = (m: string): number =>
                          !isForecast(m) && (tActual[m] ?? 0) > 0
                            ? (client.monthlyActualCogs[m] ?? 0)
                            : (client.monthlyCogs[m] ?? 0);
                        const effCogsTotal = months.reduce((s, m) => s + effCogs(m), 0);
                        const profitTotal = revTotal - effCogsTotal;

                        return [
                          // Section header
                          <tr key={`${client.teamId}-header`}>
                            <td
                              colSpan={colCount}
                              style={{
                                padding: '8px 10px 4px',
                                borderBottom: '2px solid hsla(0,0%,0%,0.08)',
                                fontSize: 12,
                                color: '#09090B',
                                fontWeight: 700,
                                textAlign: 'left',
                              }}
                            >
                              {client.clientName}
                            </td>
                          </tr>,
                          // Revenue
                          <tr key={`${client.teamId}-rev`}>
                            <td
                              style={{
                                paddingLeft: 20,
                                color: '#71717A',
                                fontWeight: 400,
                                fontSize: 12,
                              }}
                            >
                              Revenue
                            </td>
                            {months.map((m) => (
                              <td
                                key={m}
                                style={{
                                  ...cellStyle(m),
                                  color: client.monthlyRevenue[m] ? '#10B981' : '#C5C5C7',
                                }}
                              >
                                {client.monthlyRevenue[m] ? fmt(client.monthlyRevenue[m]) : '—'}
                              </td>
                            ))}
                            <td style={{ fontWeight: 500, color: '#10B981' }}>{fmt(revTotal)}</td>
                          </tr>,
                          // COGS (actual past / plan future)
                          <tr key={`${client.teamId}-cogs`}>
                            <td
                              style={{
                                paddingLeft: 20,
                                color: '#71717A',
                                fontWeight: 400,
                                fontSize: 12,
                              }}
                            >
                              COGS
                            </td>
                            {months.map((m) => {
                              const cogs = effCogs(m);
                              return (
                                <td
                                  key={m}
                                  style={{
                                    ...cellStyle(m),
                                    color: cogs ? '#EF4444' : '#C5C5C7',
                                  }}
                                >
                                  {cogs ? fmt(cogs) : '—'}
                                </td>
                              );
                            })}
                            <td style={{ fontWeight: 500, color: '#EF4444' }}>{fmt(effCogsTotal)}</td>
                          </tr>,
                          // Profit
                          <tr key={`${client.teamId}-profit`}>
                            <td
                              style={{
                                paddingLeft: 20,
                                color: '#71717A',
                                fontWeight: 400,
                                fontSize: 12,
                              }}
                            >
                              Profit
                            </td>
                            {months.map((m) => {
                              const rev = client.monthlyRevenue[m] ?? 0;
                              const cogs = effCogs(m);
                              const p = rev - cogs;
                              const hasData = rev > 0 || cogs > 0;
                              let profitColor = '#C5C5C7';
                              if (hasData) profitColor = p >= 0 ? '#054AD9' : '#EF4444';
                              return (
                                <td
                                  key={m}
                                  style={{ ...cellStyle(m), fontWeight: 500, color: profitColor }}
                                >
                                  {hasData ? fmt(p) : '—'}
                                </td>
                              );
                            })}
                            <td
                              style={{
                                fontWeight: 700,
                                color: profitTotal >= 0 ? '#054AD9' : '#EF4444',
                              }}
                            >
                              {fmt(profitTotal)}
                            </td>
                          </tr>,
                        ];
                      })}
                    </tbody>
                  </table>
                </Box>
                {forecastStartMonth && (
                  <Box
                    sx={{
                      mt: 1.5,
                      fontSize: 11,
                      color: '#92400E',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: forecastBg,
                        border: '1px solid #FDE68A',
                        borderRadius: '2px',
                        flexShrink: 0,
                      }}
                    />
                    Forecast columns are projections based on current team member rates
                  </Box>
                )}
                {hasActualCogs && (
                  <Box sx={{ mt: 1, fontSize: 11, color: '#71717A' }}>
                    COGS shows actual Warp payroll for past months with an uploaded report, and
                    plan rates for forecast months. Gross Margin &amp; EBITDA use the same basis.
                  </Box>
                )}
                {hasUnallocated && (
                  <Box sx={{ mt: 0.5, fontSize: 11, color: '#92400E' }}>
                    {fmt(totalUnallocatedActual)} of actual payroll isn&apos;t mapped to a client —
                    the person didn&apos;t match a profile (fix on the payroll report) or has no
                    client allocation (fix on the Contractors page).
                  </Box>
                )}
              </SectionCard>
            </Box>
          );
        })()}

      {/* Spend by Category + Top Vendors */}
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}
      >
        <SectionCard title="Spend by Category">
          {(() => {
            if (isLoading) return <LoadingState message="Loading categories..." />;
            if (spendByCategory.length === 0)
              return <EmptyState title="No data" description="No expense data yet." />;
            return <ReactECharts option={pieOption} style={{ height: 300 }} />;
          })()}
        </SectionCard>

        <SectionCard
          title="Top Vendors by Spend"
          action={
            <Link
              to="/financex/vendors"
              style={{ textDecoration: 'none', fontSize: 13, color: '#054AD9' }}
            >
              View all
            </Link>
          }
        >
          {(() => {
            if (isLoading) return <LoadingState message="Loading vendors..." />;
            if (topVendors.length === 0)
              return <EmptyState title="No data" description="No vendor data yet." />;
            return (
              <Box>
                {topVendors.map((vendor, idx) => {
                  const pct = totalExpenses > 0 ? (vendor.total / totalExpenses) * 100 : 0;
                  return (
                    <Box
                      key={vendor.vendorId}
                      sx={{
                        py: 1.25,
                        borderBottom: idx < topVendors.length - 1 ? '1px solid #F4F4F5' : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 0.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: VENDOR_COLORS[idx % VENDOR_COLORS.length],
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ fontSize: 13, fontWeight: 500 }}>{vendor.vendorName}</Box>
                          <Box sx={{ fontSize: 11, color: '#71717A' }}>
                            {vendor.txCount} txn{vendor.txCount !== 1 ? 's' : ''}
                          </Box>
                        </Box>
                        <Box sx={{ fontSize: 13, fontWeight: 500 }}>{fmtFull(vendor.total)}</Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          ml: 2.25,
                          backgroundColor: '#F4F4F5',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: VENDOR_COLORS[idx % VENDOR_COLORS.length],
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </SectionCard>
      </Box>

      {/* Drill-down Modal */}
      {drillDown.open && (() => {
        const accentMap = {
          revenue: { color: '#10B981', bg: '#F0FDF4', label: 'Revenue' },
          cogs: { color: '#EF4444', bg: '#FEF2F2', label: 'COGS' },
          ops: { color: '#7C3AED', bg: '#F5F3FF', label: 'Operational People' },
          opex: { color: '#EA580C', bg: '#FFF7ED', label: 'Operating Expenses' },
        };
        const accent = accentMap[drillDown.type];
        // Ops people reuse the COGS (team-member) table renderer.
        const isCogs = drillDown.type === 'cogs' || drillDown.type === 'ops';
        const isForecast = drillDown.forecastRows.length > 0;
        let itemCount = drillDown.rows.length;
        let total = drillDown.rows.reduce((s, r) => s + r.amount, 0);
        if (isForecast) {
          itemCount = drillDown.forecastRows.length;
          total = drillDown.forecastRows.reduce((s, r) => s + r.projectedRevenue, 0);
        } else if (isCogs) {
          itemCount = drillDown.cogsRows.length;
          total = drillDown.cogsRows.reduce((s, r) => s + r.cost, 0);
        }

        // OpEx drill-down groups by category or vendor, sorted by subtotal desc.
        // Vendor answers "how much are we spending on Anthropic in total", which
        // category cannot: one provider's charges scatter across many small
        // subscription lines within the same category.
        type OpexGroup = { label: string; rows: typeof drillDown.rows; subtotal: number };
        const opexGroups: OpexGroup[] =
          drillDown.type === 'opex'
            ? Object.values(
                drillDown.rows.reduce<Record<string, OpexGroup>>((acc, r) => {
                  const key =
                    opexGroupBy === 'vendor'
                      ? r.vendor || 'Unknown vendor'
                      : r.category || 'Uncategorized';
                  if (!acc[key]) acc[key] = { label: key, rows: [], subtotal: 0 };
                  acc[key].rows.push(r);
                  acc[key].subtotal += r.amount;
                  return acc;
                }, {}),
              ).sort((a, b) => b.subtotal - a.subtotal)
            : [];

        return (
          <Backdrop
            open
            onClick={onCloseDrillDown}
            sx={{ zIndex: 1300, backgroundColor: 'rgba(0,0,0,0.4)' }}
          >
            <Fade in>
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={isMobile ? {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  maxHeight: '90vh',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 -4px 24px hsla(0,0%,0%,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  paddingBottom: 'env(safe-area-inset-bottom)',
                } : {
                  width: '90vw',
                  maxWidth: 860,
                  maxHeight: '85vh',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px hsla(0,0%,0%,0.16)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid #F4F4F5',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      backgroundColor: accent.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isCogs ? (
                      <GroupsIcon sx={{ fontSize: 18, color: accent.color }} />
                    ) : (
                      <ReceiptLongIcon sx={{ fontSize: 18, color: accent.color }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 15, fontWeight: 700, color: '#09090B', lineHeight: 1.3 }}>
                      {drillDown.label}
                    </Box>
                    {!drillDown.loading && (
                      <Box sx={{ fontSize: 12, color: '#71717A', mt: 0.25 }}>
                        {itemCount} {isCogs ? 'team member' : 'transaction'}{itemCount !== 1 ? 's' : ''}
                        {drillDown.type === 'opex' && opexGroups.length > 0
                          ? ` · ${opexGroups.length} ${opexGroupBy === 'vendor' ? 'vendors' : 'categories'}`
                          : ''}
                      </Box>
                    )}
                  </Box>
                  {drillDown.type === 'opex' && !drillDown.loading && (
                    <ToggleButtonGroup
                      value={opexGroupBy}
                      exclusive
                      size="small"
                      onChange={(_, val) => val && setOpexGroupBy(val)}
                      sx={{
                        mr: 1,
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          fontSize: 12,
                          px: 1.25,
                          py: 0.25,
                          borderColor: '#E4E4E7',
                          '&.Mui-selected': {
                            backgroundColor: '#054AD915',
                            color: '#054AD9',
                            borderColor: '#054AD9',
                          },
                        },
                      }}
                    >
                      <ToggleButton value="vendor">By vendor</ToggleButton>
                      <ToggleButton value="category">By category</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  <IconButton
                    onClick={onCloseDrillDown}
                    size="small"
                    sx={{
                      color: '#71717A',
                      '&:hover': { backgroundColor: '#F4F4F5' },
                      '&:active': { backgroundColor: '#E4E4E7' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {/* Summary bar */}
                {!drillDown.loading && itemCount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 3,
                      py: 1.5,
                      backgroundColor: accent.bg,
                      borderBottom: '1px solid #F4F4F5',
                    }}
                  >
                    <Box sx={{ fontSize: 13, fontWeight: 500, color: '#4A4B4E' }}>
                      Total for period
                      {/* Say which basis produced this figure. The COGS and Ops
                          lines switch between measured payroll and rate-card
                          estimates per month, and an unlabelled total invites
                          exactly the "why doesn't this match?" question. */}
                      {isCogs && drillDown.cogsRows.length > 0 && (
                        <Box
                          component="span"
                          sx={{ ml: 1, fontSize: 11, fontWeight: 400, color: '#71717A' }}
                        >
                          {drillDown.cogsRows[0].basis === 'actual'
                            ? '· measured from payroll'
                            : '· estimated from rate cards'}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ fontSize: 18, fontWeight: 700, color: accent.color }}>
                      {fmt(total)}
                    </Box>
                  </Box>
                )}

                {/* Body */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {drillDown.loading && (
                    <Box sx={{ py: 6 }}>
                      <LoadingState message="Loading details..." />
                    </Box>
                  )}
                  {!drillDown.loading && itemCount === 0 && (
                    <Box sx={{ py: 6 }}>
                      <EmptyState title="No data" description={`No ${accent.label.toLowerCase()} entries for this month.`} />
                    </Box>
                  )}
                  {!drillDown.loading && itemCount > 0 && isCogs && (
                    <Box
                      sx={{
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                          minWidth: 600,
                        },
                        '& thead th': {
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#FAFAFA',
                          textAlign: 'left',
                          padding: '10px 16px',
                          color: '#71717A',
                          fontWeight: 500,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderBottom: '1px solid #E4E4E7',
                          whiteSpace: 'nowrap',
                          zIndex: 1,
                        },
                        '& tbody tr': {
                          transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                          '&:hover': { backgroundColor: '#FAFAFA' },
                          '&:active': { backgroundColor: '#F4F4F5' },
                        },
                        '& tbody td': {
                          padding: '10px 16px',
                          borderBottom: '1px solid #F4F4F5',
                          whiteSpace: 'nowrap',
                          fontSize: 13,
                          color: '#09090B',
                        },
                      }}
                    >
                      <table>
                        <thead>
                          <tr>
                            <th>Team Member</th>
                            <th>Team</th>
                            <th>Role</th>
                            <th style={{ textAlign: 'right' }}>Rate</th>
                            <th style={{ textAlign: 'right' }}>Alloc.</th>
                            <th style={{ textAlign: 'right' }}>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDown.cogsRows.map((row) => (
                            <tr key={row.id}>
                              <td>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '999px',
                                      backgroundColor: accent.bg,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: accent.color,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {row.memberName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </Box>
                                  <Box sx={{ fontWeight: 500 }}>{row.memberName}</Box>
                                </Box>
                              </td>
                              <td>{row.teamName}</td>
                              <td style={{ color: '#71717A' }}>{row.role || '—'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                                {fmtFull(row.monthlyRate)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: row.allocationPct === 100 ? '#F0FDF4' : '#FEF3C7',
                                    color: row.allocationPct === 100 ? '#16A34A' : '#D97706',
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  {row.allocationPct}%
                                </Box>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'monospace', fontSize: 12, color: accent.color }}>
                                {fmtFull(row.cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                  {!drillDown.loading && itemCount > 0 && isForecast && (
                    <Box
                      sx={{
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                          minWidth: 600,
                        },
                        '& thead th': {
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#FAFAFA',
                          textAlign: 'left',
                          padding: '10px 16px',
                          color: '#71717A',
                          fontWeight: 500,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderBottom: '1px solid #E4E4E7',
                          whiteSpace: 'nowrap',
                          zIndex: 1,
                        },
                        '& tbody tr': {
                          transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                          '&:hover': { backgroundColor: '#FAFAFA' },
                          '&:active': { backgroundColor: '#F4F4F5' },
                        },
                        '& tbody td': {
                          padding: '10px 16px',
                          borderBottom: '1px solid #F4F4F5',
                          whiteSpace: 'nowrap',
                          fontSize: 13,
                          color: '#09090B',
                        },
                      }}
                    >
                      <Box sx={{ px: 2, py: 1, backgroundColor: '#FFFBEB', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ fontSize: 12, color: '#D97706', fontWeight: 500 }}>
                          Forecast — projected from client rates × 160h/mo × allocation
                        </Box>
                      </Box>
                      <table>
                        <thead>
                          <tr>
                            <th>Team Member</th>
                            <th>Client</th>
                            <th style={{ textAlign: 'right' }}>Rate</th>
                            <th style={{ textAlign: 'center' }}>Type</th>
                            <th style={{ textAlign: 'right' }}>Alloc.</th>
                            <th style={{ textAlign: 'right' }}>Projected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDown.forecastRows.map((row) => (
                            <tr key={row.id}>
                              <td>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '999px',
                                      backgroundColor: accent.bg,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: accent.color,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {row.memberName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </Box>
                                  <Box sx={{ fontWeight: 500 }}>{row.memberName}</Box>
                                </Box>
                              </td>
                              <td>{row.teamName}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                                {fmtFull(row.clientRate)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: row.clientRateType === 'hourly' ? '#EFF6FF' : '#F0FDF4',
                                    color: row.clientRateType === 'hourly' ? '#2563EB' : '#16A34A',
                                    fontSize: 11,
                                    fontWeight: 500,
                                  }}
                                >
                                  {row.clientRateType === 'hourly' ? '$/hr × 160h' : '$/mo'}
                                </Box>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: row.allocationPct === 100 ? '#F0FDF4' : '#FEF3C7',
                                    color: row.allocationPct === 100 ? '#16A34A' : '#D97706',
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  {row.allocationPct}%
                                </Box>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'monospace', fontSize: 12, color: accent.color }}>
                                {fmtFull(row.projectedRevenue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                  {!drillDown.loading && itemCount > 0 && !isCogs && !isForecast && (
                    <Box
                      sx={{
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                          minWidth: 550,
                        },
                        '& thead th': {
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#FAFAFA',
                          textAlign: 'left',
                          padding: '10px 16px',
                          color: '#71717A',
                          fontWeight: 500,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderBottom: '1px solid #E4E4E7',
                          whiteSpace: 'nowrap',
                          zIndex: 1,
                        },
                        '& tbody tr': {
                          transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                          '&:hover': { backgroundColor: '#FAFAFA' },
                          '&:active': { backgroundColor: '#F4F4F5' },
                        },
                        '& tbody td': {
                          padding: '10px 16px',
                          borderBottom: '1px solid #F4F4F5',
                          whiteSpace: 'nowrap',
                          fontSize: 13,
                          color: '#09090B',
                        },
                      }}
                    >
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th>
                              {drillDown.type === 'opex' && opexGroupBy === 'vendor'
                                ? 'Category'
                                : 'Vendor'}
                            </th>
                            {drillDown.type !== 'opex' && (
                              <th>{drillDown.type === 'revenue' ? 'Source' : 'Category'}</th>
                            )}
                            <th style={{ textAlign: 'right' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDown.type === 'opex'
                            ? opexGroups.flatMap((group) => [
                                <tr key={`grp-${group.label}`}>
                                  <td
                                    colSpan={3}
                                    style={{
                                      backgroundColor: accent.bg,
                                      fontWeight: 700,
                                      fontSize: 12,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      color: accent.color,
                                    }}
                                  >
                                    {group.label}
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        color: '#71717A',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                      }}
                                    >
                                      {group.rows.length} item{group.rows.length !== 1 ? 's' : ''}
                                    </Box>
                                  </td>
                                  <td
                                    style={{
                                      backgroundColor: accent.bg,
                                      textAlign: 'right',
                                      fontWeight: 700,
                                      fontFamily: 'monospace',
                                      fontSize: 12,
                                      color: accent.color,
                                    }}
                                  >
                                    {fmtFull(group.subtotal)}
                                  </td>
                                </tr>,
                                ...group.rows.map((row) => (
                                  <tr key={row.id}>
                                    <td style={{ color: '#71717A', fontSize: 12 }}>{row.date}</td>
                                    <td style={{ fontWeight: 500, whiteSpace: 'normal', maxWidth: 320 }}>
                                      {row.description}
                                    </td>
                                    {/* The grouping key is already the row header,
                                        so show the other dimension here instead of
                                        repeating it on every line. */}
                                    <td>
                                      {opexGroupBy === 'vendor'
                                        ? row.category || '—'
                                        : row.vendor || '—'}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'monospace', fontSize: 12, color: accent.color }}>
                                      {fmtFull(row.amount)}
                                    </td>
                                  </tr>
                                )),
                              ])
                            : drillDown.rows.map((row) => (
                                <tr key={row.id}>
                                  <td style={{ color: '#71717A', fontSize: 12 }}>{row.date}</td>
                                  <td style={{ fontWeight: 500, whiteSpace: 'normal', maxWidth: 260 }}>
                                    {row.description}
                                  </td>
                                  <td>{row.vendor || '—'}</td>
                                  <td>
                                    {row.category ? (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-block',
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: '4px',
                                          backgroundColor: '#F4F4F5',
                                          fontSize: 12,
                                          fontWeight: 500,
                                          color: '#4A4B4E',
                                        }}
                                      >
                                        {row.category}
                                      </Box>
                                    ) : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'monospace', fontSize: 12, color: accent.color }}>
                                    {fmtFull(row.amount)}
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          </Backdrop>
        );
      })()}
    </>
  );
};

export default FinanceXDashboardComponent;
